import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiDirectionsRenderer,
  MiDirectionsService,
  MiMap,
  MiMarker,
} from "@miplatform/components";
import {
  calculateMapZoomLevel,
  getDurationInHours,
  getLocationBoundaries,
} from "@miplatform/utils";
import { EmailActivityPreviewModal } from "@mibuildweb/components";
import { Box, useTheme } from "@mui/material";
import { CLOSEOUT_TAB } from "../CloseoutDrawer/constants";
import { TaskOverview } from "../TaskOverview/TaskOverview";
import { DirectionsRoute } from "./DirectionsRoute";
import { getWorkLocationMarker } from "./getMarkerIcon";
import { useMapViewModel } from "./useMapViewModel";

const getLocationBoundariesFromAttempts = (data) => {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  let hasLocations = false;

  data.forEach((entry) => {
    const attempts = entry[1];

    if (attempts && attempts.length > 0) {
      attempts.forEach((assignment) => {
        if (assignment.home?.address?.location) {
          const { location } = assignment.home.address;
          hasLocations = true;

          minLat = Math.min(minLat, location.latitude);
          maxLat = Math.max(maxLat, location.latitude);
          minLng = Math.min(minLng, location.longitude);
          maxLng = Math.max(maxLng, location.longitude);
        }
      });
    }
  });

  if (!hasLocations) {
    return {};
  }

  const latDiff = maxLat - minLat;
  const lngDiff = maxLng - minLng;
  const maxDiff = Math.max(latDiff, lngDiff);

  const zoomFactor = calculateMapZoomLevel(maxDiff);

  return {
    position: {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2,
    },
    zoomFactor,
  };
};

const TRAVEL_MODE_DRIVING = "DRIVING";

export const DispatchMap = ({
  UserCard = () => null,
  config = {},
  data = [],
  date,
  users = [],
  serviceAreas = [],
  onCloseoutDrawerOpen = () => {},
  onEmailDrawerOpen = () => {},
}) => {
  const { groupBy } = config;
  const timeoutRef = useRef();
  const [mapData, setMapData] = useState([]);
  const [routes, setRoutes] = useState({});
  const [anchorEl, setAnchorEl] = useState();
  const [taskDetail, setTaskDetail] = useState({});
  const [emailPreviewDetails, setEmailPreviewDetails] = useState(null);
  const [selectedUsersIds, setSelectedUsersIds] = useState([]);

  const { delayedTime } = taskDetail;
  const diffInStartTime = getDurationInHours(
    taskDetail.scheduledStartDate,
    taskDetail.startDate
  );

  const { position, zoomFactor } = useMemo(() => {
    if (serviceAreas.length > 0) {
      return getLocationBoundaries(
        serviceAreas.map((area) => ({
          ...area.location,
          Radius: Number.isNaN(parseFloat(area.radius))
            ? 0
            : parseFloat(area.radius),
        }))
      );
    }

    return getLocationBoundariesFromAttempts(mapData);
  }, [mapData, serviceAreas]);
  const filteredMapData = useMemo(() => {
    if (selectedUsersIds.length === 0) {
      return mapData;
    }
    const selectedUsersIdsSet = new Set(selectedUsersIds);

    return mapData.filter(([user]) => selectedUsersIdsSet.has(user.id));
  }, [mapData, selectedUsersIds]);

  const directionsRoutes = useMemo(
    () =>
      filteredMapData.reduce((acc, [user, data]) => {
        acc[user.id] = data
          .map((attempt) => ({
            lat: attempt.home?.address?.location?.latitude,
            lng: attempt.home?.address?.location?.longitude,
          }))
          .filter((loc) => loc.lat && loc.lng);

        return acc;
      }, {}),
    [filteredMapData]
  );

  const isDelayed = delayedTime > 0 || diffInStartTime > 0;
  const isEarly = diffInStartTime < 0;

  // FIX:ME investigate what causes rerendering without useRef
  const routesLoaded = useRef({});
  const theme = useTheme();
  const { classes } = useStyles();
  const { getUserData } = useMapViewModel();

  const prepareUsers = () => {
    setMapData(getUserData({ data, groupBy, users }));
  };

  const getDirections = (res, id) => {
    if (res.status === "OK") {
      setRoutes((prevRoutes) => ({
        ...prevRoutes,
        [id]: res,
      }));
    }
    routesLoaded.current[id] = true;
  };

  const openPopper = (e, location) => {
    const { currentTarget } = e.domEvent;
    const timeout = setTimeout(() => {
      setAnchorEl(currentTarget);
      setTaskDetail(location);
    }, 500);
    timeoutRef.current = timeout;
  };

  const closePopper = () => {
    setAnchorEl(null);
    setTaskDetail({});
    clearTimeout(timeoutRef.current);
  };

  const handleCloseoutDrawerOpen = (attempt, options) => {
    closePopper();
    onCloseoutDrawerOpen(attempt, options);
  };

  const handleEmailPreviewModalOpen = (details) => {
    closePopper();
    setEmailPreviewDetails(details);
  };

  const handleEmailPreviewModalClose = () => {
    setEmailPreviewDetails(null);
  };

  const handleUserSelect = (user) => {
    setSelectedUsersIds((prevSelected) =>
      prevSelected.includes(user.id)
        ? prevSelected.filter((id) => id !== user.id)
        : [...prevSelected, user.id]
    );
  };

  useEffect(() => {
    if (users?.length && groupBy && data) {
      prepareUsers();
      routesLoaded.current = {};
      setRoutes({});
    }
  }, [data, groupBy, users]);

  return (
    <Box data-testid="map" className={classes.container}>
      <Box className={classes.columnsContainer}>
        {mapData?.map(([user]) => (
          <Box key={user[groupBy]} className={classes.columnHeaderContainer}>
            <UserCard
              hideMenu
              isSelected={selectedUsersIds.includes(user.id)}
              user={user}
              date={date}
              onSelect={handleUserSelect}
            />
          </Box>
        ))}
      </Box>
      <Box className={classes.mapContainer}>
        <MiMap
          zoomFactor={zoomFactor}
          initialMapPosition={position}
          onClick={closePopper}
          onDrag={closePopper}
          onZoomChanged={closePopper}
        >
          {filteredMapData.map(([user, data]) => (
            <Fragment key={user.id}>
              <DirectionsRoute
                points={directionsRoutes[user.id]}
                color={user.color}
              />
              {data.map((attempt, index) => (
                <Box key={attempt.id} onMouseLeave={closePopper}>
                  <MiMarker
                    position={{
                      lat: attempt.home?.address?.location?.latitude,
                      lng: attempt.home?.address?.location?.longitude,
                    }}
                    label={{
                      text: (index + 1).toString(),
                      color: theme.palette.white.main,
                    }}
                    icon={getWorkLocationMarker(
                      user.color,
                      attempt.attemptStatus,
                      attempt.id === taskDetail.id
                    )}
                    onMouseOver={(e) => {
                      openPopper(e, attempt);
                    }}
                    onClick={() => handleCloseoutDrawerOpen(attempt)}
                  />
                  {taskDetail?.id === attempt.id && (
                    <TaskOverview
                      hideEmails
                      attempt={taskDetail}
                      anchorEl={anchorEl}
                      isEarly={isEarly}
                      isDelayed={isDelayed}
                      delayedTime={delayedTime}
                      openHistoryAttempt={(attempt) =>
                        handleCloseoutDrawerOpen(attempt, {
                          tab: CLOSEOUT_TAB.HISTORY,
                        })
                      }
                      onEmailDrawerOpen={onEmailDrawerOpen}
                      onEmailPreviewModalOpen={handleEmailPreviewModalOpen}
                    />
                  )}
                </Box>
              ))}
              {!routesLoaded.current[user.id] && (
                <MiDirectionsService
                  key={user.id}
                  origin={{
                    lat: data[0]?.address?.location?.latitude,
                    lng: data[0]?.address?.location?.longitude,
                  }}
                  waypoints={data?.slice(1, -1).map((location) => ({
                    location: {
                      lat: location?.address?.location?.latitude,
                      lng: location?.address?.location?.longitude,
                    },
                  }))}
                  destination={{
                    lat: data[data.length - 1]?.address?.location?.latitude,
                    lng: data[data.length - 1]?.address?.location?.longitude,
                  }}
                  travelMode={TRAVEL_MODE_DRIVING}
                  callback={(res) => getDirections(res, user.id)}
                />
              )}
            </Fragment>
          ))}
          {Object.entries(routes).map(([key, route]) => {
            const [user] = mapData.find(([r]) => r.id === key) || {};
            return (
              <MiDirectionsRenderer
                key={key}
                route={route}
                options={{
                  polylineOptions: { strokeColor: user?.color },
                  suppressMarkers: true,
                  preserveViewport: true,
                }}
              />
            );
          })}
        </MiMap>
        {emailPreviewDetails && (
          <EmailActivityPreviewModal
            isEmailModalOpen
            activity={emailPreviewDetails}
            details={emailPreviewDetails}
            closeEmailModal={handleEmailPreviewModalClose}
          />
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    overflow: "hidden",
  },
  columnsContainer: {
    width: "100%",
    backgroundColor: theme.palette.common.white,
    display: "flex",
    gap: 8,
    overflowX: "auto",
    "&::-webkit-scrollbar": { display: "none" } /* webkit */,
    msOverflowStyle: "none" /* IE and Edge */,
    scrollbarWidth: "none" /* Firefox */,
  },
  columnHeaderContainer: {
    backgroundColor: theme.palette.blue.light,
    borderRadius: "8px 8px 0 0",
  },
  mapContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
}));
